// Fonction de survie pour la loi de Weibull
function rWeibull(t, shape, scale) {
  // R(t) = exp(- (t / scale)^shape)
  return Math.exp(-Math.pow(t / scale, shape));
}

// Fonction de survie pour la loi de Gompertz
function rGompertz(t, shape, scale) {
  // R(t) = exp(-shape * (exp(scale * t) - 1))
  return Math.exp(-shape * (Math.exp(scale * t) - 1));
}

// Calcul du paramètre scale (échelle) pour Weibull à partir d'un point
function resolveScaleWeibull(t1, R1, shape) {
  // scale = t1 / (-ln(R1)^(1/shape))
  return t1 / Math.pow(-Math.log(R1), 1 / shape);
}

// Calcul du paramètre shape (forme) pour Weibull à partir de deux points
function resolveShapeWeibull(t1, t2, R1, R2) {
  // shape = log(log(R1) / log(R2)) / log(t1 / t2)
  return Math.log(Math.log(R1) / Math.log(R2)) / Math.log(t1 / t2);
}

// Fonction de taux de panne pour la loi de Weibull
function failureRateWeibull(t, shape, scale) {
  // h(t) = shape / scale * (t / scale)^(shape - 1)
  return (shape / scale) * Math.pow(t / scale, shape - 1);
}

// Fonction de taux de survie pour la loi de Gompertz
function failureRateGompertz(t, shape, scale) {
  // h(t) = shape * scale * exp(scale * t)
  return shape * scale * Math.exp(scale * t);
}

function weibullInverseCDF(p, shape, scale) {
  if (p < 0 || p > 1) {
    throw new Error('p must be between 0 and 1');
  }
  return scale * Math.pow(-Math.log(1 - p), 1 / shape);
}

function gompertzInverseCDF(p, shape, scale) {
  if (p <= 0 || p >= 1) {
    throw new Error('p must be between 0 and 1 (exclusive)');
  }

  const b = shape;
  const eta = scale;

  const lnOneMinusP = Math.log(1 - p);
  const inner = 1 - (b / eta) * lnOneMinusP;

  if (inner <= 0) {
    throw new Error('Invalid parameters leading to log of non-positive number');
  }

  const t = (1 / b) * Math.log(inner);
  return t;
}

function generateSurvivalData(modeFiabilite, echelle, forme) {
  // R(t) = exp(- (t / scale)^shape)
  const data = [];
  const tMax = maxTickTime(modeFiabilite, forme, echelle);
  const step = 0.1; // More granular steps

  for (let t = 0; t <= tMax; t += step) {
    let R = 0;
    if (modeFiabilite === 'Gompertz') {
      R = rGompertz(t, forme, echelle);
    } else if (modeFiabilite === 'Weibull') {
      R = rWeibull(t, forme, echelle);
    }
    data.push({ t: parseFloat(t.toFixed(2)), R });
  }
  return data;
}

function generateFailureData(modeFiabilite, echelle, forme) {
  // h(t) = shape / scale * (t / scale)^(shape - 1)
  const data = [];
  const tMax = maxTickTime(modeFiabilite, forme, echelle);
  const step = 0.1;

  for (let t = 0; t <= tMax; t += step) {
    let failureRate = 0;
    if (modeFiabilite === 'Gompertz') {
      failureRate = failureRateGompertz(t, forme, echelle);
    } else if (modeFiabilite === 'Weibull') {
      failureRate = failureRateWeibull(t, forme, echelle);
    }
    data.push({ t: parseFloat(t.toFixed(2)), R: failureRate });
  }
  return data;
}

function computeInitialPoints(modeFiabilite, echelle, forme) {
  if (!echelle || !forme) return null;
  // Compute times corresponding to reasonnable probabilities
  // also using the fact that survival function is 1-CDF we take higher R1 and lower R2
  // to get the right t1 and t2 (t1 < t2)
  // R1 = 0.9 and R2 = 0.1 are arbitrary but reasonable values
  const R1 = 0.9;
  const R2 = 0.1;
  let t1, t2;

  if (modeFiabilite === 'Weibull') {
    t1 = weibullInverseCDF(1 - R1, forme, echelle);
    t2 = weibullInverseCDF(1 - R2, forme, echelle);
  } else if (modeFiabilite === 'Gompertz') {
    t1 = gompertzInverseCDF(1 - R1, forme, echelle);
    t2 = gompertzInverseCDF(1 - R2, forme, echelle);
  }

  return {
    t1: t1.toFixed(2),
    t2: t2.toFixed(2),
    R1: R1.toFixed(2),
    R2: R2.toFixed(2),
  };
}

// Évaluation de b pour l'optimisation
function evalB(b, t1, t2, R1, R2) {
  const A = Math.exp(b * t1) - 1;
  const B = Math.exp(b * t2) - 1;
  const C = Math.log(R1);
  const D = Math.log(R2);

  return b === 0 ? t1 / t2 - C / D : A / B - C / D;
}

// Évaluation de la dérivée de b pour la recherche de solution
function evalBPrime(b, t1, t2) {
  if (b === 0) return 0.5 * (t1 / t2) * (t1 - t2);
  const A = Math.exp(b * t1) - 1;
  const APrime = t1 * Math.exp(b * t1);
  const B = Math.exp(b * t2) - 1;
  const BPrime = t2 * Math.exp(b * t2);

  return (APrime * B - A * BPrime) / Math.pow(B, 2);
}

// Résolution de la valeur de scale (b) en utilisant la méthode de Newton-Raphson
function resolveScaleGompertz(t1, t2, R1, R2) {
  if (t2 <= t1) throw new Error('t1 must be smaller than t2');
  if (t1 / t2 <= Math.log(R1) / Math.log(R2)) throw new Error('No solution with a positive scale can be found');

  const tol = 1e-6;
  const nIterMax = 1000;
  let b = 0.01; // Initialisation avec une petite valeur pour éviter la division par zéro

  for (let i = 0; i < nIterMax; i++) {
    const fB = evalB(b, t1, t2, R1, R2);
    if (Math.abs(fB) < tol) break;

    const fBPrime = evalBPrime(b, t1, t2);
    if (Math.abs(fBPrime) < tol) throw new Error('Cannot find a solution due to too small derivative value.');

    b = b - fB / fBPrime;
  }

  return b;
}

// Résolution de la forme (shape) pour Gompertz
function resolveShapeGompertz(b, t1, R1) {
  return -Math.log(R1) / (Math.exp(b * t1) - 1);
}

// MTTF stands for Mean Time To Failure.
// It is a reliability metric that indicates the average time expected until a system or component fails.
// MTTF is commonly used in the context of non-repairable systems,
// providing insights into the lifespan and reliability of products or services.
// A higher MTTF suggests greater reliability and durability.
function mttfWeibull(shape, scale) {
  return 7 ** (1 / shape) * scale;
}

function mttfGompertz(shape, scale) {
  return Math.log(7 / shape + 1) / scale;
}

function maxTickTime(modeFiabilite, forme, echelle) {
  if (!echelle || !forme) return 100;
  if (modeFiabilite === 'Weibull') {
    return Math.max(Math.min(Math.ceil((mttfWeibull(forme, echelle) * 1.5) / 10) * 10, 1000), 30);
  } else {
    return Math.max(Math.min(Math.ceil((mttfGompertz(forme, echelle) * 1.5) / 10) * 10, 1000), 30);
  }
}

export {
  rWeibull,
  rGompertz,
  resolveScaleWeibull,
  resolveShapeWeibull,
  failureRateGompertz,
  failureRateWeibull,
  generateSurvivalData,
  generateFailureData,
  computeInitialPoints,
  resolveScaleGompertz,
  resolveShapeGompertz,
  mttfWeibull,
  mttfGompertz,
  maxTickTime,
};
